import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { extractErrorMessage } from '../../../../utils/errorHandler';
import { TextField, Grid, Button, FormHelperText, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, FormControlLabel, Switch, Box, Snackbar, Alert } from '@mui/material';
import { useCategoriesCreateMutation, useSalonsListQuery, useAuthProfileRetrieveQuery, useCategoriesDefaultRetrieveQuery, useSalonsCategoriesListQuery } from '../../../../services/GLOWClient';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import MuiSelect from '../../../../SystemDesign/MuiSelect';
import AddIcon from '@mui/icons-material/Add';
import TranslateIcon from '@mui/icons-material/Translate';
import TranslationValidationModal from '../TranslationValidationModal';
import theme from '../../../../theme';

const AddCategory = ({ open, onClose, onSuccess, selectedSalonId = null, isCreationMode = false, nested = false, sx, ...otherProps }) => {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        salon: selectedSalonId || '',
        useExisting: true,  // Set to true by default
        selectedCategories: [],
        categoryDescriptions: {} // Store individual descriptions for each selected category
    });
    const [errors, setErrors] = useState({});
    const [dialogClosed, setDialogClosed] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error'
    });
    const [translationModalOpen, setTranslationModalOpen] = useState(false);
    const [translationModalLoading, setTranslationModalLoading] = useState(false);
    const [pendingTranslations, setPendingTranslations] = useState(null);
    const [createdCategoryId, setCreatedCategoryId] = useState(null);
    const [createdCategoryDraft, setCreatedCategoryDraft] = useState({ name: '', description: '' });
    const [pendingCreatedCategory, setPendingCreatedCategory] = useState(null);
    const [pendingSuccessMessage, setPendingSuccessMessage] = useState('');
    const pollIntervalRef = useRef(null);
    const normalizedCurrentLanguage = (i18n.language || 'fr').split('-')[0].toLowerCase();

    // Get current user profile to filter salons by owner
    const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();

    // Fetch salons owned by the current user
    const { data: salonsData } = useSalonsListQuery(
        userProfile ? { owner: userProfile.id } : {},
        { skip: !userProfile }
    );

    // Get current language for refetching
    const currentLanguage = i18n.language;

    // Fetch default categories using the dedicated endpoint
    const { data: defaultCategories = [], refetch: refetchDefaultCategories } = useCategoriesDefaultRetrieveQuery(undefined, {
        skip: !open // Only fetch when dialog is open
    });

    // Refetch default categories when language changes
    useEffect(() => {
        if (open && refetchDefaultCategories) {
            refetchDefaultCategories();
        }
    }, [currentLanguage, open]);

    useEffect(() => () => {
        clearInterval(pollIntervalRef.current);
    }, []);

    // Fetch existing categories for the selected salon (to exclude already-created ones)
    const { data: salonCategoriesData } = useSalonsCategoriesListQuery(
        (selectedSalonId || formData.salon) ? { salonId: Number(selectedSalonId || formData.salon) } : undefined,
        { skip: !open || !(selectedSalonId || formData.salon) }
    );

    const salonCategories = React.useMemo(() => {
        if (!salonCategoriesData) return [];
        if (Array.isArray(salonCategoriesData)) return salonCategoriesData;
        return salonCategoriesData.results || [];
    }, [salonCategoriesData]);

    const availableDefaultCategories = React.useMemo(() => {
        const existingNames = new Set(
            salonCategories
                .map((c) => (c?.name || '').trim().toLowerCase())
                .filter(Boolean)
        );
        return (Array.isArray(defaultCategories) ? defaultCategories : []).filter((cat) => {
            const name = (cat?.name || '').trim().toLowerCase();
            if (!name) return false;
            return !existingNames.has(name);
        });
    }, [defaultCategories, salonCategories]);

    // RTK Query hook for creating a category
    const [createCategory, { isLoading: isCreating }] = useCategoriesCreateMutation();

    // Toggle between creating new category and selecting existing ones
    const toggleCategoryMode = (event) => {
        const useExisting = event.target.checked;
        setFormData(prev => ({
            ...prev,
            useExisting,
            name: useExisting ? '' : prev.name,
            selectedCategories: useExisting ? prev.selectedCategories : [],
            categoryDescriptions: useExisting ? prev.categoryDescriptions : {}
        }));

        // Clear relevant errors
        setErrors(prev => ({
            ...prev,
            name: useExisting ? '' : prev.name,
            selectedCategories: !useExisting ? '' : prev.selectedCategories
        }));
    };

    // Reset dialog state when open/close changes
    useEffect(() => {
        if (open === false) {
            setDialogClosed(true);
        } else {
            setDialogClosed(false);
        }
    }, [open]);

    // Set selected salon ID when it's provided as prop
    useEffect(() => {
        if (selectedSalonId) {
            setFormData(prev => ({
                ...prev,
                salon: selectedSalonId
            }));
        }
    }, [selectedSalonId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCategoryDescriptionChange = (categoryId, description) => {
        setFormData(prev => ({
            ...prev,
            categoryDescriptions: {
                ...prev.categoryDescriptions,
                [categoryId]: description
            }
        }));
    };

    // Helper function to generate basic translations
    const generateTranslations = (name, description) => {
        const currentLang = i18n.language;
        return {
            name_translations: { [currentLang]: name || '' },
            description_translations: { [currentLang]: description || '' }
        };
    };

    const handleCategorySelect = (event) => {
        const { value } = event.target;
        setFormData(prev => {
            // Initialize descriptions for newly selected categories
            const newDescriptions = { ...prev.categoryDescriptions };
            value.forEach(categoryId => {
                if (!newDescriptions[categoryId]) {
                    const category = availableDefaultCategories.find(cat => cat.category_id === categoryId);
                    newDescriptions[categoryId] = category?.localized_description || category?.description || '';
                }
            });

            // Remove descriptions for unselected categories
            Object.keys(newDescriptions).forEach(categoryId => {
                if (!value.includes(parseInt(categoryId))) {
                    delete newDescriptions[categoryId];
                }
            });

            return {
                ...prev,
                selectedCategories: value,
                categoryDescriptions: newDescriptions
            };
        });

        if (errors.selectedCategories) {
            setErrors(prev => ({
                ...prev,
                selectedCategories: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Always require salon selection
        if (!formData.salon && !selectedSalonId) {
            newErrors.salon = t('pro_category.add_category.select_salon_error');
        }

        if (formData.useExisting) {
            if (!formData.selectedCategories?.length) {
                newErrors.selectedCategories = t('pro_category.add_category.select_at_least_one_error');
            }
        } else {
            if (!formData.name?.trim()) {
                newErrors.name = t('pro_category.add_category.name_required_error');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasGeneratedTranslations = (translations = {}) => {
        return ['fr', 'en', 'ar'].some(
            (lang) => lang !== normalizedCurrentLanguage && Boolean(translations?.[lang])
        );
    };

    const finalizeCategorySuccess = (message, data) => {
        setSnackbar({
            open: true,
            message: t('pro_category.add_category.category_created_success'),
            severity: 'success'
        });

        if (onSuccess) {
            onSuccess(message, data);
        } else if (onClose) {
            onClose();
        }
    };

    const handleSaveTranslations = async (editedTranslations) => {
        setTranslationModalOpen(false);
        clearInterval(pollIntervalRef.current);

        if (!createdCategoryId) return;

        try {
            const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
            await fetch(`/api/categories/${createdCategoryId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name_translations: editedTranslations.name,
                    description_translations: editedTranslations.description,
                }),
            });
        } catch (e) {
            console.warn('[category translations] patch error', e);
        }

        finalizeCategorySuccess(pendingSuccessMessage, pendingCreatedCategory);
    };

    const handleTranslationSkip = () => {
        setTranslationModalOpen(false);
        clearInterval(pollIntervalRef.current);
        finalizeCategorySuccess(pendingSuccessMessage, pendingCreatedCategory);
    };

    const handleSubmit = async (withTranslationCheck = false) => {
        if (!validateForm()) return;

        try {
            let successMessage;
            let successResult;
            let customCategoryDraft = { name: '', description: '' };
            const shouldCheckTranslations = withTranslationCheck && !formData.useExisting;
            if (isCreationMode) {
                // In creation mode, handle both new categories and existing categories
                if (formData.useExisting) {
                    // Create categories from selected existing ones
                    const createPromises = formData.selectedCategories.map(categoryId => {
                        const numericCategoryId = parseInt(categoryId);
                        const category = availableDefaultCategories.find(cat => cat.category_id === numericCategoryId);

                        if (!category) {
                            throw new Error(`Category with ID ${categoryId} not found in default categories`);
                        }

                        const categoryDescription = formData.categoryDescriptions[categoryId]?.trim() || category.localized_description || category.description || '';
                        const categoryData = {
                            categoryCreateRequest: {
                                name: (category.localized_name || category.name).trim(),
                                description: categoryDescription,
                                salon: parseInt(formData.salon || selectedSalonId),
                                name_translations: category.name_translations && Object.keys(category.name_translations).length > 0 ? category.name_translations : generateTranslations((category.localized_name || category.name).trim(), categoryDescription).name_translations,
                                description_translations: category.description_translations && Object.keys(category.description_translations).length > 0 ? category.description_translations : generateTranslations((category.localized_name || category.name).trim(), categoryDescription).description_translations
                            }
                        };

                        return createCategory(categoryData).unwrap();
                    });

                    const results = await Promise.all(createPromises);
                    // Pass the first created category as the result for the callback
                    successMessage = t('pro_category.add_category.category_added_success');
                    successResult = results[0];
                } else {
                    // Create a single new category
                    const salonId = formData.salon || selectedSalonId;
                    const trimmedName = formData.name.trim();
                    const trimmedDescription = formData.description?.trim() || '';
                    const categoryData = {
                        categoryCreateRequest: {
                            name: trimmedName,
                            description: trimmedDescription,
                            salon: salonId ? parseInt(salonId) : null, ...generateTranslations(trimmedName, trimmedDescription)
                        }
                    };

                    customCategoryDraft = { name: trimmedName, description: trimmedDescription };
                    const result = await createCategory(categoryData).unwrap();
                    setCreatedCategoryDraft(customCategoryDraft);
                    successMessage = t('pro_category.add_category.category_added_success');
                    successResult = result;
                }

            } else {
                // In update mode, create the category via API
                if (formData.useExisting) {
                    // Create categories from selected existing ones
                    const createPromises = formData.selectedCategories.map(categoryId => {
                        const numericCategoryId = parseInt(categoryId);
                        const category = availableDefaultCategories.find(cat => cat.category_id === numericCategoryId);

                        if (!category) {
                            throw new Error(`Category with ID ${categoryId} not found in default categories`);
                        }

                        const categoryDescription = formData.categoryDescriptions[categoryId]?.trim() || category.localized_description || category.description || '';
                        const categoryData = {
                            categoryCreateRequest: {
                                name: (category.localized_name || category.name).trim(),
                                description: categoryDescription,
                                salon: parseInt(formData.salon || selectedSalonId),
                                name_translations: category.name_translations && Object.keys(category.name_translations).length > 0 ? category.name_translations : generateTranslations((category.localized_name || category.name).trim(), categoryDescription).name_translations,
                                description_translations: category.description_translations && Object.keys(category.description_translations).length > 0 ? category.description_translations : generateTranslations((category.localized_name || category.name).trim(), categoryDescription).description_translations
                            }
                        };

                        return createCategory(categoryData).unwrap()
                            .catch(error => {
                                return Promise.reject({ error, categoryName: category.localized_name || category.name });
                            });
                    });

                    // Wait for all promises to settle
                    const results = await Promise.allSettled(createPromises);

                    // Check if any requests failed
                    const failedCategories = results
                        .filter(result => result.status === 'rejected')
                        .map(rejected => ({
                            name: rejected.reason.categoryName,
                            error: extractErrorMessage(rejected.reason.error)
                        }));

                    if (failedCategories.length > 0) {
                        const errorMessage = failedCategories.map(c => `${c.name}: ${c.error}`).join('; ');
                        setErrors(prev => ({
                            ...prev,
                            defaultCategoriesError: errorMessage
                        }));

                        // Show snackbar with failed categories
                        setSnackbar({
                            open: true,
                            message: t('pro_category.add_category.creation_error', { error: errorMessage }),
                            severity: 'error'
                        });
                        return;
                    }

                    // Pass the first successful result for the callback
                    const successfulResults = results.filter(result => result.status === 'fulfilled');
                    if (successfulResults.length > 0) {
                        successMessage = t('pro_category.add_category.category_added_success');
                        successResult = successfulResults[0].value;
                    }

                    // Show success snackbar for existing categories
                    setSnackbar({
                        open: true,
                        message: t('pro_category.add_category.categories_added_success'),
                        severity: 'success'
                    });
                } else {
                    // Create a single new category
                    const trimmedName = formData.name.trim();
                    const trimmedDescription = formData.description?.trim() || '';
                    const categoryData = {
                        categoryCreateRequest: {
                            name: trimmedName,
                            description: trimmedDescription,
                            salon: parseInt(formData.salon || selectedSalonId), ...generateTranslations(trimmedName, trimmedDescription)
                        }
                    };

                    customCategoryDraft = { name: trimmedName, description: trimmedDescription };
                    const result = await createCategory(categoryData).unwrap();
                    setCreatedCategoryDraft(customCategoryDraft);
                    successMessage = t('pro_category.add_category.category_added_success');
                    successResult = result;
                }
            }

            // Reset form and call success callback if no errors
            setFormData({
                name: '',
                description: '',
                salon: selectedSalonId || '',
                useExisting: true,  // Keep useExisting as true by default
                selectedCategories: [],
                categoryDescriptions: {}
            });

            if (shouldCheckTranslations && successResult) {
                const newCategoryId = successResult?.category_id || successResult?.id;
                setPendingCreatedCategory(successResult);
                setPendingSuccessMessage(successMessage);
                setCreatedCategoryId(newCategoryId);
                setTranslationModalOpen(true);
                setTranslationModalLoading(true);
                setPendingTranslations(null);

                let attempts = 0;
                const maxAttempts = 20;
                pollIntervalRef.current = setInterval(async () => {
                    attempts++;

                    try {
                        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
                        const response = await fetch(`/api/categories/${newCategoryId}/`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });

                        if (response.ok) {
                            const data = await response.json();
                            const categoryTranslations = {
                                name: data.name_translations || {},
                                description: data.description_translations || {},
                            };

                            if (hasGeneratedTranslations(categoryTranslations.name) || attempts >= maxAttempts) {
                                clearInterval(pollIntervalRef.current);
                                setPendingTranslations(categoryTranslations);
                                setTranslationModalLoading(false);
                            }
                        }
                    } catch (e) {
                        console.warn('[category translations] poll error', e);
                        if (attempts >= maxAttempts) {
                            clearInterval(pollIntervalRef.current);
                            setPendingTranslations({
                                name: { [normalizedCurrentLanguage]: customCategoryDraft.name },
                                description: { [normalizedCurrentLanguage]: customCategoryDraft.description },
                            });
                            setTranslationModalLoading(false);
                        }
                    }
                }, 1500);
            } else {
                finalizeCategorySuccess(successMessage, successResult);
            }
        } catch (error) {
            console.error('Failed to create category:', error);

            // Handle API validation errors
            if (error.data) {
                const apiErrors = {};
                let snackbarMessage = '';

                Object.entries(error.data).forEach(([key, value]) => {
                    const errorMessage = Array.isArray(value) ? value.join(' ') : value;
                    apiErrors[key] = errorMessage;

                    // Build user-friendly snackbar message
                    if (key === 'name') {
                        snackbarMessage = errorMessage;
                    } else if (key === 'salon') {
                        snackbarMessage = t('pro_category.add_category.salon_error', { error: errorMessage });
                    } else if (key === 'description') {
                        snackbarMessage = t('pro_category.add_category.description_error', { error: errorMessage });
                    } else {
                        snackbarMessage = errorMessage;
                    }
                });

                setErrors(apiErrors);

                // Show snackbar with user-friendly error message
                if (snackbarMessage) {
                    setSnackbar({
                        open: true,
                        message: snackbarMessage,
                        severity: 'error'
                    });
                }
            } else {
                // Generic error message for network or other issues
                setSnackbar({
                    open: true,
                    message: extractErrorMessage(error, t('pro_category.add_category.generic_creation_error')),
                    severity: 'error'
                });
            }
        }
    };

    const handleDialogClose = () => {
        // Reset form and errors when dialog is closed
        clearInterval(pollIntervalRef.current);
        setFormData({
            name: '',
            description: '',
            salon: selectedSalonId || '',
            useExisting: true,  // Keep useExisting as true by default
            selectedCategories: [],
            categoryDescriptions: {}
        });
        setErrors({});
        setSnackbar({ open: false, message: '', severity: 'error' });
        setTranslationModalOpen(false);
        setTranslationModalLoading(false);
        setPendingTranslations(null);
        setCreatedCategoryId(null);
        setCreatedCategoryDraft({ name: '', description: '' });
        setPendingCreatedCategory(null);
        setPendingSuccessMessage('');
        onClose();
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Content to be displayed in the dialog
    const dialogContent = (
        <Grid container display="flex" spacing={2} sx={{ p: 2, maxHeight: '60vh', overflowY: 'auto', width: '100%', maxWidth: '400px' }}>

            {/* Display general errors at the top */}
            {errors.defaultCategoriesError && (
                <Grid item xs={12}>
                    <FormHelperText error sx={{ textAlign: 'center' }}>
                        {errors.defaultCategoriesError}
                    </FormHelperText>
                </Grid>
            )}

            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.useExisting}
                            onChange={toggleCategoryMode}
                            color="primary"
                        />
                    }
                    label={t('pro_category.add_category.select_existing_categories')}
                />
            </Grid>

            {formData.useExisting ? (
                <>
                    <Grid item xs={12}>
                        <MuiSelect
                            multiple
                            label={t('pro_category.add_category.available_categories')}
                            list={availableDefaultCategories.map(cat => ({
                                id: cat.category_id,
                                name: cat.localized_name || cat.name
                            }))}
                            selectedValue={formData.selectedCategories}
                            onChange={handleCategorySelect}
                            required
                            error={!!errors.selectedCategories}
                        />
                        {errors.selectedCategories && (
                            <FormHelperText error>{errors.selectedCategories}</FormHelperText>
                        )}
                    </Grid>
                    {/* Individual description fields for each selected category */}
                    {formData.selectedCategories.map(categoryId => {
                        const category = availableDefaultCategories.find(cat => cat.category_id === categoryId);
                        if (!category) return null;

                        return (
                            <Grid item xs={12} key={categoryId}>
                                <TextField
                                    label={t('pro_category.add_category.description_for_category', { name: category.localized_name || category.name })}
                                    value={formData.categoryDescriptions[categoryId] || ''}
                                    onChange={(e) => handleCategoryDescriptionChange(categoryId, e.target.value)}
                                    fullWidth
                                    multiline

                                    placeholder={t('pro_category.add_category.description_placeholder', { name: category.localized_name || category.name, defaultDescription: category.localized_description || category.description || t('pro_category.add_category.no_description') })}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            minHeight: '80px', // Fixed minimum height
                                        }
                                    }}
                                />
                            </Grid>
                        );
                    })}
                </>
            ) : (
                <>
                    <Grid item xs={12}>
                        <TextField
                            label={t('pro_category.add_category.category_name')}
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            fullWidth
                            required
                            error={!!errors.name}
                            helperText={errors.name}
                            placeholder={t('pro_category.add_category.category_name_placeholder')}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    minHeight: '56px', // Fixed height for single line
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t('pro_category.add_category.description_optional')}
                            name="description"

                            value={formData.description}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={3}
                            maxRows={5}
                            error={!!errors.description}
                            helperText={errors.description}
                            placeholder={t('pro_category.add_category.describe_category_placeholder')}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    minHeight: '80px', // Fixed minimum height
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => handleSubmit(true)}
                            disabled={isCreating}
                            startIcon={<TranslateIcon fontSize="small" />}
                            sx={{
                                width: '100%',
                                borderColor: theme.colors.primary[400],
                                color: theme.colors.primary[700],
                                fontSize: '0.75rem',
                                padding: '8px 12px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: theme.colors.primary[600],
                                    backgroundColor: theme.colors.primary[50]
                                }
                            }}
                        >
                            {t('pro_salon.translations.save_and_check', 'Enregistrer et vérifier la traduction')}
                        </Button>
                    </Grid>
                </>
            )}
        </Grid>
    );

    // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
    const triggerButton = <Button style={{ display: 'none' }}>{t('pro_category.add_category.add')}</Button>;

    return (
        <>
            <MuiDialog
                open={open}
                icon={<AddIcon />}
                triggerButton={triggerButton}
                title={t('pro_category.add_category.add_category_title')}
                content={dialogContent}
                existAction={true}
                actionTitle={t('pro_category.add_category.add')}
                actionFunction={() => handleSubmit(false)}
                onClose={dialogClosed}
                onExternalClose={handleDialogClose}
                isLoading={isCreating}
                actionType="button"
                maxWidth="xs"
                disableAction={isCreating}
                nested={nested}
                sx={sx}
                {...otherProps}
            />

            <TranslationValidationModal
                open={translationModalOpen}
                onClose={handleTranslationSkip}
                onValidate={handleSaveTranslations}
                salonName={createdCategoryDraft.name}
                salonDescription={createdCategoryDraft.description}
                isLoading={translationModalLoading}
                translations={pendingTranslations}
            />

            {/* Snackbar for error messages */}
            <Snackbar

                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddCategory;
